/* VisaApplicationDemo — brand-neutral 6-screen simulator.
 * Steps: intro → identity → data_entry → center → slot → confirm → result. */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITY_AR = { riyadh: "الرياض", jeddah: "جدة", dammam: "الدمام" };

export default function VisaApplicationDemo() {
  const [params] = useSearchParams();
  const presetScenario = params.get("scenario");
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [slots, setSlots] = useState([]);
  const [step, setStep] = useState("intro");
  const [feedback, setFeedback] = useState([]);
  const [score, setScore] = useState(0);
  const [trapHint, setTrapHint] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Step-specific local state
  const [identityForm, setIdentityForm] = useState({});
  const [dataForm, setDataForm] = useState({ full_name: "", passport_number: "", date_of_birth: "" });
  const [city, setCity] = useState("");
  const [slotId, setSlotId] = useState("");
  const [noSlotsAction, setNoSlotsAction] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const nav = useNavigate();

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("vta_token")}` } });

  useEffect(() => { document.title = "Visa Application Demo"; }, []);

  const start = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/demo/start`,
        { scenario_id: presetScenario || null }, auth());
      setSession(data.session_id);
      setScenario(data.scenario);
      setStep("identity");
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر بدء الديمو");
    } finally { setBusy(false); }
  };

  const submit = async (stepKey, payload) => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/demo/${session}/answer`,
        { step: stepKey, payload }, auth());
      setFeedback(data.feedback || []);
      setScore(data.total_score);
      if (data.completed) {
        setTrapHint(data.trap_hint || "");
        setStep("result");
      } else {
        setStep(data.next_step);
        if (data.next_step === "slot") {
          // Fetch state to get slots
          const { data: st } = await axios.get(`${API}/demo/${session}/state`, auth());
          setSlots(st.slots || []);
        }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإرسال");
    } finally { setBusy(false); }
  };

  if (!session) {
    return (
      <div style={s.wrap} data-testid="demo-intro">
        <div style={s.card}>
          <div style={s.brandNeutral}>Visa Application Demo</div>
          <h1 style={s.h1}>محاكي تقديم تأشيرة عملي</h1>
          <p style={s.lead}>
            ستحصل على حالة عشوائية واقعية. اقرأ التفاصيل بدقة وأكمل المراحل الست.
            هدفك: إثبات أنك تعرف من المتقدم، تدخل البيانات بدقة، تختار المدينة الصحيحة، وتتعامل مع نقص المواعيد.
          </p>
          {err && <div style={s.err}>{err}</div>}
          <button onClick={start} disabled={busy} style={s.cta} data-testid="demo-start-btn">
            {busy ? "…" : "ابدأ الجلسة"}
          </button>
        </div>
      </div>
    );
  }

  const traveler = scenario.traveler || (scenario.travelers || [])[0] || {};

  return (
    <div style={s.wrap} data-testid="demo-running">
      <div style={s.card}>
        <div style={s.brandNeutral}>Visa Application Demo</div>
        <div style={s.scenBar}>
          <span style={s.scenCode}>{scenario.code}</span>
          <span style={s.scoreBox}>النتيجة: <b>{score}</b></span>
        </div>
        <h2 style={s.h2}>{scenario.title}</h2>
        <p style={s.intro}>{scenario.intro_ar}</p>

        <Stepper step={step} />

        {/* ─ STEP: identity ─ */}
        {step === "identity" && (
          <IdentityStep scenario={scenario} traveler={traveler}
                        onSubmit={(p) => submit("identity", p)} busy={busy} />
        )}

        {/* ─ STEP: data_entry ─ */}
        {step === "data_entry" && (
          <DataEntryStep traveler={traveler} value={dataForm} onChange={setDataForm}
                         onSubmit={() => submit("data_entry", dataForm)} busy={busy} />
        )}

        {/* ─ STEP: center ─ */}
        {step === "center" && (
          <CenterStep city={city} setCity={setCity}
                      onSubmit={() => submit("center", { city })} busy={busy} />
        )}

        {/* ─ STEP: slot ─ */}
        {step === "slot" && (
          <SlotStep slots={slots} slotId={slotId} setSlotId={setSlotId}
                    noAction={noSlotsAction} setNoAction={setNoSlotsAction}
                    onSubmit={() => submit("slot",
                      slots.length ? { slot_id: slotId } : { action: noSlotsAction })}
                    busy={busy} />
        )}

        {/* ─ STEP: confirm ─ */}
        {step === "confirm" && (
          <ConfirmStep scenario={scenario} confirmed={confirmed} setConfirmed={setConfirmed}
                       onSubmit={() => submit("confirm", { confirmed: !!confirmed })} busy={busy} />
        )}

        {/* ─ STEP: result ─ */}
        {step === "result" && (
          <ResultStep score={score} trapHint={trapHint}
                      onAnother={() => { setSession(null); setStep("intro"); setScore(0);
                                          setFeedback([]); setTrapHint(""); }}
                      onAssessment={() => nav("/associate/assessment")} />
        )}

        {feedback.length > 0 && step !== "result" && (
          <div style={s.fbBox} data-testid="step-feedback">
            {feedback.map((f, i) => (
              <div key={i} style={{ color: f.startsWith("✓") ? "#10b981" : "#ef4444", fontSize: 13 }}>{f}</div>
            ))}
          </div>
        )}
        {err && <div style={s.err}>{err}</div>}
      </div>
    </div>
  );
}

// ─────────── Step components ───────────

function Stepper({ step }) {
  const order = ["identity", "data_entry", "center", "slot", "confirm"];
  const labels = { identity: "هوية المتقدم", data_entry: "بيانات الجواز", center: "المدينة", slot: "الموعد", confirm: "التأكيد" };
  return (
    <div style={s.stepper}>
      {order.map((k, i) => (
        <div key={k} style={{ ...s.stepPill, ...(step === k ? s.stepPillActive : null) }}>
          {i + 1}. {labels[k]}
        </div>
      ))}
    </div>
  );
}

function IdentityStep({ scenario, traveler, onSubmit, busy }) {
  const [actualApplicant, setActualApplicant] = useState("");
  const [count, setCount] = useState("");
  const [maidSep, setMaidSep] = useState(null);
  const [addedSponsor, setAddedSponsor] = useState(false);
  const [addedIqama, setAddedIqama] = useState(false);
  const [decision, setDecision] = useState("");
  const [extras, setExtras] = useState([]);

  const toggle = (k) => setExtras(extras.includes(k) ? extras.filter(x => x !== k) : [...extras, k]);

  // Render different question per scenario id
  return (
    <div style={s.stepBody}>
      <h3 style={s.qHead}>س1: حدد المتقدم والإعدادات الصحيحة</h3>
      {scenario.scenario_id === "tr_maid" && (
        <div>
          <p style={s.qLine}>من المتقدم الفعلي في هذا الملف؟</p>
          {[["traveler", "الخادمة (صاحبة الجواز)"], ["sponsor", "الكفيل السعودي"]].map(([k, l]) => (
            <label key={k} style={s.radio}>
              <input type="radio" checked={actualApplicant === k} onChange={() => setActualApplicant(k)}
                     data-testid={`identity-${k}`} /> {l}
            </label>
          ))}
          <button onClick={() => onSubmit({ actual_applicant: actualApplicant })}
                  disabled={busy || !actualApplicant} style={s.next} data-testid="step-submit">التالي</button>
        </div>
      )}
      {scenario.scenario_id === "fr_family" && (
        <div>
          <p style={s.qLine}>كم ملف شنغن منفصل تحتاج؟</p>
          <input type="number" value={count} onChange={e => setCount(e.target.value)} style={s.input} data-testid="fam-count" />
          <p style={s.qLine}>هل الخادمة فرد من العائلة في ملف موحد؟</p>
          {[["yes", "نعم — فرد من العائلة"], ["no", "لا — ملف مستقل تماماً"]].map(([v, l]) => (
            <label key={v} style={s.radio}>
              <input type="radio" checked={maidSep === (v === "no")} onChange={() => setMaidSep(v === "no")}
                     data-testid={`maid-sep-${v}`} /> {l}
            </label>
          ))}
          <button onClick={() => onSubmit({ application_count: parseInt(count, 10), maid_is_separate_file: maidSep })}
                  disabled={busy || !count} style={s.next} data-testid="step-submit">التالي</button>
        </div>
      )}
      {scenario.scenario_id === "fr_individual" && (
        <div>
          <p style={s.qLine}>هل تضيف خانة كفيل لهذا الملف؟</p>
          <label style={s.radio}><input type="checkbox" checked={addedSponsor} onChange={e => setAddedSponsor(e.target.checked)} data-testid="add-sponsor" /> أضف خانة كفيل</label>
          <p style={s.qLine}>هل تطلب رقم إقامة؟</p>
          <label style={s.radio}><input type="checkbox" checked={addedIqama} onChange={e => setAddedIqama(e.target.checked)} data-testid="add-iqama" /> أضف رقم الإقامة</label>
          <button onClick={() => onSubmit({ added_sponsor: addedSponsor, added_iqama: addedIqama })}
                  disabled={busy} style={s.next} data-testid="step-submit">التالي</button>
        </div>
      )}
      {scenario.scenario_id === "ca_bil" && (
        <div>
          <p style={s.qLine}>دفع رسوم البصمة: {scenario.biometric_fee_paid ? "نعم" : "لا"} · وصول BIL: {scenario.bil_received ? "نعم" : "لا"}</p>
          <p style={s.qLine}>ما القرار الصحيح؟</p>
          {[["book_now", "أحجز الموعد الآن"], ["wait_for_bil", "أنتظر BIL أولاً"], ["use_old_bil", "أستخدم BIL سابق"]].map(([k, l]) => (
            <label key={k} style={s.radio}>
              <input type="radio" checked={decision === k} onChange={() => setDecision(k)} data-testid={`decision-${k}`} /> {l}
            </label>
          ))}
          <button onClick={() => onSubmit({ decision })} disabled={busy || !decision} style={s.next} data-testid="step-submit">التالي</button>
        </div>
      )}
      {scenario.scenario_id === "ksa_resident" && (
        <div>
          <p style={s.qLine}>اختر المستندات الإضافية للمقيم غير السعودي (يمكن اختيار عدة):</p>
          {[["iqama", "نسخة الإقامة"], ["iqama_translation", "ترجمة الإقامة"],
            ["exit_reentry", "تأشيرة خروج وعودة"], ["family_book", "كرت العائلة"]].map(([k, l]) => (
            <label key={k} style={s.radio}>
              <input type="checkbox" checked={extras.includes(k)} onChange={() => toggle(k)}
                     data-testid={`extra-${k}`} /> {l}
            </label>
          ))}
          <button onClick={() => onSubmit({ required_extras: extras })}
                  disabled={busy || !extras.length} style={s.next} data-testid="step-submit">التالي</button>
        </div>
      )}
    </div>
  );
}

function DataEntryStep({ traveler, value, onChange, onSubmit, busy }) {
  return (
    <div style={s.stepBody}>
      <h3 style={s.qHead}>س2: أدخل بيانات الجواز كما تظهر في الصورة</h3>
      <div style={s.passportCard}>
        <div style={{ fontSize: 11, color: "#888" }}>صورة الجواز (نص مستخرَج)</div>
        <div style={{ marginTop: 6, lineHeight: 1.8, fontFamily: "monospace", fontSize: 13 }}>
          <div>Surname / Name: {traveler.full_name}</div>
          <div>Passport No: {traveler.passport_number}</div>
          <div>Nationality: {traveler.nationality}</div>
          <div>DOB: {traveler.date_of_birth}</div>
          <div>Expiry: {traveler.expiry_date}</div>
          <div>Sex: {traveler.sex}</div>
        </div>
      </div>
      <label style={s.label}>الاسم الكامل (UPPERCASE)</label>
      <input value={value.full_name} onChange={e => onChange({ ...value, full_name: e.target.value })}
             style={s.input} data-testid="de-name" />
      <label style={s.label}>رقم الجواز</label>
      <input value={value.passport_number} onChange={e => onChange({ ...value, passport_number: e.target.value })}
             style={s.input} data-testid="de-passport" />
      <label style={s.label}>تاريخ الميلاد (YYYY-MM-DD)</label>
      <input value={value.date_of_birth} onChange={e => onChange({ ...value, date_of_birth: e.target.value })}
             style={s.input} data-testid="de-dob" />
      <button onClick={onSubmit} disabled={busy} style={s.next} data-testid="step-submit">التالي</button>
    </div>
  );
}

function CenterStep({ city, setCity, onSubmit, busy }) {
  return (
    <div style={s.stepBody}>
      <h3 style={s.qHead}>س3: اختر مدينة مركز البصمة</h3>
      <p style={s.qLine}>يجب أن تطابق المدينة المختارة من العميل في الطلب.</p>
      {[["riyadh", "الرياض"], ["jeddah", "جدة"], ["dammam", "الدمام"]].map(([k, l]) => (
        <label key={k} style={s.radio}>
          <input type="radio" checked={city === k} onChange={() => setCity(k)} data-testid={`city-${k}`} /> {l}
        </label>
      ))}
      <button onClick={onSubmit} disabled={busy || !city} style={s.next} data-testid="step-submit">التالي</button>
    </div>
  );
}

function SlotStep({ slots, slotId, setSlotId, noAction, setNoAction, onSubmit, busy }) {
  if (!slots.length) {
    return (
      <div style={s.stepBody}>
        <h3 style={s.qHead}>س4: لا توجد مواعيد متاحة</h3>
        <p style={s.qLine}>النظام لا يعرض أي مواعيد. ماذا تفعل؟</p>
        {[["book_anyway", "أحجز أي شيء متاح خارج المدينة"],
          ["raise_no_slots_note", "أرفع ملاحظة تشغيلية للمكتب"],
          ["call_client", "أتصل بالعميل"]].map(([k, l]) => (
          <label key={k} style={s.radio}>
            <input type="radio" checked={noAction === k} onChange={() => setNoAction(k)} data-testid={`no-slot-${k}`} /> {l}
          </label>
        ))}
        <button onClick={onSubmit} disabled={busy || !noAction} style={s.next} data-testid="step-submit">التالي</button>
      </div>
    );
  }
  return (
    <div style={s.stepBody}>
      <h3 style={s.qHead}>س4: اختر موعداً من المواعيد المتاحة</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
        {slots.map((sl) => (
          <label key={sl.id} style={{ ...s.slotCard, ...(slotId === sl.id ? s.slotCardActive : null) }}>
            <input type="radio" checked={slotId === sl.id} onChange={() => setSlotId(sl.id)}
                   data-testid={`slot-${sl.id.slice(0, 8)}`} style={{ marginInlineEnd: 6 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{new Date(sl.datetime).toLocaleString("en-US")}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{CITY_AR[sl.city]}</div>
            </div>
          </label>
        ))}
      </div>
      <button onClick={onSubmit} disabled={busy || !slotId} style={s.next} data-testid="step-submit">التالي</button>
    </div>
  );
}

function ConfirmStep({ scenario, confirmed, setConfirmed, onSubmit, busy }) {
  return (
    <div style={s.stepBody}>
      <h3 style={s.qHead}>س5: تأكيد الموعد</h3>
      <p style={s.qLine}>هل تؤكد الحجز الآن؟</p>
      {scenario.scenario_id === "ca_bil" && (
        <p style={{ color: "#fbbf24", fontSize: 12 }}>تذكير: BIL لم يصل بعد لهذه الحالة.</p>
      )}
      {[[true, "نعم — أؤكد الحجز"], [false, "لا — لا أؤكد الآن"]].map(([k, l]) => (
        <label key={String(k)} style={s.radio}>
          <input type="radio" checked={confirmed === k} onChange={() => setConfirmed(k)} data-testid={`confirm-${k}`} /> {l}
        </label>
      ))}
      <button onClick={onSubmit} disabled={busy || confirmed === null} style={s.next} data-testid="step-submit">إنهاء</button>
    </div>
  );
}

function ResultStep({ score, trapHint, onAnother, onAssessment }) {
  return (
    <div style={s.stepBody} data-testid="demo-result">
      <h3 style={s.qHead}>النتيجة: {score} / 80</h3>
      {trapHint && (
        <div style={s.trap}>
          <div style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700 }}>الفخ التشغيلي:</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>{trapHint}</div>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button onClick={onAnother} style={s.next} data-testid="another-demo-btn">حالة أخرى</button>
        <button onClick={onAssessment} style={s.ctaSecondary} data-testid="goto-assessment-btn">انتقل للتقييم النهائي</button>
      </div>
    </div>
  );
}

const s = {
  wrap: { minHeight: "100vh", background: "#0a0a0f", color: "#e9e9ec", padding: "32px 14px",
          direction: "rtl", fontFamily: "'Tajawal', system-ui, sans-serif" },
  card: { maxWidth: 760, margin: "0 auto", background: "#13131a", border: "1px solid #2a2a3a",
          borderRadius: 16, padding: 24 },
  brandNeutral: { fontSize: 11, color: "#9a9a9f", letterSpacing: 2, marginBottom: 10, textTransform: "uppercase" },
  h1: { fontSize: 24, fontWeight: 900, margin: 0, color: "#d4af37" },
  h2: { fontSize: 18, fontWeight: 800, color: "#fff", margin: "8px 0 6px" },
  lead: { fontSize: 13, color: "#bbb", lineHeight: 1.8, marginTop: 10 },
  intro: { fontSize: 13, color: "#bbb", lineHeight: 1.8, padding: 12,
           background: "#1a1a25", borderRadius: 10, marginTop: 10 },
  scenBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  scenCode: { fontSize: 11, color: "#d4af37", letterSpacing: 1, fontWeight: 700 },
  scoreBox: { fontSize: 12, color: "#bbb", padding: "4px 10px", background: "#1a1a25", borderRadius: 999 },
  stepper: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 16, marginBottom: 14 },
  stepPill: { fontSize: 11, padding: "5px 10px", background: "#1a1a25", border: "1px solid #2a2a3a",
              borderRadius: 999, color: "#9a9a9f" },
  stepPillActive: { background: "#d4af37", color: "#0a0a0f", border: "1px solid #d4af37", fontWeight: 700 },
  stepBody: { marginTop: 6 },
  qHead: { fontSize: 15, fontWeight: 800, color: "#d4af37", margin: "10px 0 6px" },
  qLine: { fontSize: 13, color: "#cbd5e1", marginTop: 10, marginBottom: 6 },
  radio: { display: "block", padding: "8px 10px", marginTop: 6,
           background: "#0f0f17", border: "1px solid #2a2a3a", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  input: { width: "100%", padding: "9px 12px", background: "#0f0f17", border: "1px solid #2a2a3a",
           borderRadius: 8, color: "#fff", fontSize: 13, boxSizing: "border-box", marginTop: 4 },
  label: { display: "block", fontSize: 12, color: "#bbb", marginTop: 10 },
  passportCard: { background: "#0f0f17", border: "1px dashed #d4af37", borderRadius: 10, padding: 14,
                  marginTop: 6, marginBottom: 12 },
  slotCard: { display: "flex", alignItems: "center", padding: 10, background: "#0f0f17",
              border: "1px solid #2a2a3a", borderRadius: 8, cursor: "pointer" },
  slotCardActive: { background: "#1a1a25", border: "1px solid #d4af37" },
  next: { marginTop: 16, padding: "10px 22px", background: "#d4af37", color: "#0a0a0f",
          border: "none", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 13 },
  cta: { marginTop: 18, padding: "12px 28px", background: "#d4af37", color: "#0a0a0f",
         border: "none", borderRadius: 10, fontWeight: 900, cursor: "pointer", fontSize: 14 },
  ctaSecondary: { padding: "10px 22px", background: "transparent", color: "#d4af37",
                  border: "1px solid #d4af37", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 13 },
  err: { padding: 10, background: "#3a1414", border: "1px solid #6b1f1f", borderRadius: 8,
         color: "#fca5a5", fontSize: 12, marginTop: 10 },
  fbBox: { marginTop: 14, padding: 12, background: "#0f0f17", border: "1px solid #2a2a3a",
           borderRadius: 8 },
  trap: { marginTop: 16, padding: 12, background: "#1f1810", border: "1px solid #d4af37",
          borderRadius: 10 },
};
